import { Tooltip } from '@mantine/core'

import IconSparkle from 'src/icons/IconSparkle'
import { ServerFinding } from 'src/lib/documentIntelligence'

// brand-500 — the unified AI accent shared with the other DocumentIntelligence
// row indicators (the category-column sparkle and the expiration sparkle) so
// every AI marker on a document row reads as one family.
const AI_BLUE = '#1C93D7'

// Reviewable predictions (Category / Expiration) are the AI suggestions a
// recruiter can accept or reject. Everything else (license details, etc.) is
// extraction-only and carries no review decision.
const REVIEWABLE_FIELDS = ['CATEGORY', 'DATE']

interface AiPrediction {
  field?: string | null
  reviewDecision?: string | null
}

interface AiAnalyzedIndicatorProps {
  documentIntelligence?: {
    status?: string | null
    findings?: ServerFinding[] | null
    predictions?: AiPrediction[] | null
  } | null
}

/**
 * Sparkle marking that document intelligence has analyzed a document.
 *
 * The per-field indicators (category / expiration) only render a sparkle when
 * the AI produced a scalar CATEGORY / DATE prediction, so documents whose AI
 * output is something else — most notably MVR reports, which emit grouped
 * findings rather than a date or category — showed no AI marker at all. This
 * fills that gap: any COMPLETED analysis gets a sparkle.
 *
 * Exception: a KEPT_ORIGINAL decision means the recruiter rejected the AI
 * suggestion and kept their own value, so the AI prediction isn't reflected in
 * what's displayed. When every reviewable (Category/Expiration) suggestion was
 * rejected and the doc has no findings, the AI contributed nothing on screen —
 * so the sparkle is suppressed. A pending or accepted suggestion, any finding,
 * or a doc with nothing reviewable still shows the sparkle.
 *
 * The tooltip is a plain "AI predicted" across every analyzed document.
 */
const AiAnalyzedIndicator = ({
  documentIntelligence,
}: AiAnalyzedIndicatorProps) => {
  if (documentIntelligence?.status !== 'COMPLETED') return null

  const findings = (documentIntelligence.findings ?? []) as ServerFinding[]
  const predictions = documentIntelligence.predictions ?? []

  const reviewable = predictions.filter((p) =>
    REVIEWABLE_FIELDS.includes(p.field ?? '')
  )
  const allReviewableRejected =
    reviewable.length > 0 &&
    reviewable.every((p) => p.reviewDecision === 'KEPT_ORIGINAL')
  if (findings.length === 0 && allReviewableRejected) return null

  return (
    <Tooltip label="AI predicted" withArrow className="text-xs">
      <span
        data-testid="ai-analyzed-indicator"
        style={{ color: AI_BLUE, flexShrink: 0, lineHeight: 0 }}
        className="inline-flex items-center align-middle"
      >
        <IconSparkle size={11} />
      </span>
    </Tooltip>
  )
}

export default AiAnalyzedIndicator
